/**
 * ThreeLayer — the 3D VFX overlay (the ".5" in 2.5D).
 *
 * A transparent WebGL canvas stacked over the Pixi world. Its orthographic
 * camera MIRRORS the Pixi camera (same pan/zoom, Y-down) so particles spawned at
 * entity world-coordinates line up exactly with the 2D sprites beneath.
 *
 * Ships at bootstrap: an additive particle pool (explosions / plasma) and a
 * screen-shake impulse. Extension points are marked for 3D laser beams and
 * charged-beam meshes (Mechanic 1 combat VFX).
 */
import {
  AdditiveBlending,
  AmbientLight,
  BufferAttribute,
  BufferGeometry,
  CanvasTexture,
  Color,
  CylinderGeometry,
  DirectionalLight,
  DoubleSide,
  DynamicDrawUsage,
  InstancedMesh,
  Mesh,
  MeshBasicMaterial,
  MeshStandardMaterial,
  Object3D,
  OrthographicCamera,
  PointLight,
  Points,
  PointsMaterial,
  Quaternion,
  Scene,
  Sprite,
  SpriteMaterial,
  TetrahedronGeometry,
  Vector3,
  WebGLRenderer,
} from "three";
import type { Camera } from "./RenderRoot";
import { PERF, MINING_DRONE } from "@/config/balance";

/** A thin 3D beam from an orbiting drone to its asteroid target. */
export interface DroneBeam {
  ax: number;
  ay: number;
  bx: number;
  by: number;
}

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  maxLife: number;
  size: number;
  r: number;
  g: number;
  b: number;
}

/** A lit 3D debris chunk thrown off the asteroid at the mining impact point. */
interface SparkChunk {
  x: number;
  y: number;
  z: number;
  vx: number;
  vy: number;
  vz: number;
  life: number;
  max: number;
  size: number;
}

export class ThreeLayer {
  readonly canvas: HTMLCanvasElement;
  private renderer: WebGLRenderer;
  private scene = new Scene();
  private camera = new OrthographicCamera();

  private pool: Particle[] = [];
  private cursor = 0;
  private positions: Float32Array;
  private colors: Float32Array;
  private geom = new BufferGeometry();
  private points: Points;

  // screen-shake state (decaying offset applied to the camera)
  private shakeAmp = 0;
  private shakeDecay = 6;

  // --- lit molten-spark system (mining impact VFX) ---
  private sparkMesh!: InstancedMesh;
  private sparks: SparkChunk[] = [];
  private sparkCursor = 0;
  private impactLight!: PointLight;
  private hullImpacts: { x: number; y: number }[] = []; // every hull-beam strike this frame
  private dummy = new Object3D();
  private tmpColor = new Color();
  private lookAtV = new Vector3();

  // --- thin 3D drone orbit-beams (pooled cylinders) ---
  private beamMeshes: Mesh[] = [];
  private droneBeams: DroneBeam[] = [];
  private beamUp = new Vector3(0, 1, 0);
  private beamDir = new Vector3();
  private beamQuat = new Quaternion();
  private starGlow?: Sprite; // central-star glow (only shown in the system layer)

  constructor() {
    this.canvas = document.createElement("canvas");
    this.renderer = new WebGLRenderer({
      canvas: this.canvas,
      alpha: true, // transparent — Pixi shows through
      antialias: true,
      powerPreference: "high-performance",
    });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, PERF.maxPixelRatio));
    // Fully transparent clear so this overlay never occludes the Pixi world.
    this.renderer.setClearColor(0x000000, 0);

    const max = PERF.maxParticles;
    this.positions = new Float32Array(max * 3);
    this.colors = new Float32Array(max * 3);
    for (let i = 0; i < max; i++) {
      this.pool.push({
        x: 0, y: 0, vx: 0, vy: 0, life: 0, maxLife: 1, size: 0, r: 1, g: 1, b: 1,
      });
      this.positions[i * 3] = 1e7; // park idle points far off-screen
      this.positions[i * 3 + 1] = 1e7;
    }
    this.geom.setAttribute("position", new BufferAttribute(this.positions, 3));
    this.geom.setAttribute("color", new BufferAttribute(this.colors, 3));
    const material = new PointsMaterial({
      size: 6,
      vertexColors: true,
      transparent: true,
      blending: AdditiveBlending,
      depthWrite: false,
      sizeAttenuation: true,
    });
    this.points = new Points(this.geom, material);
    this.points.frustumCulled = false;
    this.scene.add(this.points);

    // Lights so the standard-material sparks are shaded by the 3D scene and pop
    // (specular glints) against the flat 2D asteroid sprite beneath.
    this.scene.add(new AmbientLight(0x2a3340, 0.7));
    const key = new DirectionalLight(0xffffff, 1.15);
    key.position.set(-0.4, -0.7, 1); // parallel rays from the upper-left/front
    this.scene.add(key);
    this.impactLight = new PointLight(0xff9a3c, 0, 320, 2); // molten glow, animated
    this.scene.add(this.impactLight);

    // Instanced faceted debris — small tetrahedra tumble and catch the light.
    const N = 320;
    const geo = new TetrahedronGeometry(1.1, 0); // small debris chunks
    const mat = new MeshStandardMaterial({
      color: 0xff7a2a,
      emissive: 0xff5410,
      emissiveIntensity: 0.9,
      metalness: 0.7,
      roughness: 0.35,
      side: DoubleSide, // projection flips Y to match Pixi; render both faces
    });
    this.sparkMesh = new InstancedMesh(geo, mat, N);
    this.sparkMesh.instanceMatrix.setUsage(DynamicDrawUsage);
    this.sparkMesh.frustumCulled = false;
    for (let i = 0; i < N; i++) {
      this.sparks.push({
        x: 0, y: 0, z: 0, vx: 0, vy: 0, vz: 0, life: 0, max: 1, size: 1,
      });
      this.hideSpark(i);
    }
    this.scene.add(this.sparkMesh);

    // Pool of thin cylinders for drone orbit-beams (thinner than the hull laser).
    const beamGeo = new CylinderGeometry(
      MINING_DRONE.beamRadius,
      MINING_DRONE.beamRadius,
      1, // unit height — scaled to the beam length each frame
      6,
    );
    const beamMat = new MeshBasicMaterial({
      color: 0x8fe0ff,
      transparent: true,
      opacity: 0.85,
      blending: AdditiveBlending,
      depthWrite: false,
    });
    const BEAMS = 24; // plenty for many concurrent drones
    for (let i = 0; i < BEAMS; i++) {
      const m = new Mesh(beamGeo, beamMat);
      m.visible = false;
      m.frustumCulled = false;
      this.beamMeshes.push(m);
      this.scene.add(m);
    }

    // Central star: a persistent radial-gradient glow sprite + a warm PointLight,
    // fixed at the world origin (0,0) where the star always sits.
    const glow = new Sprite(
      new SpriteMaterial({
        map: makeStarGlowTexture(),
        transparent: true,
        blending: AdditiveBlending,
        depthWrite: false,
      }),
    );
    glow.scale.set(780, 780, 1);
    glow.position.set(0, 0, 0);
    this.starGlow = glow;
    this.scene.add(glow);
    const starLight = new PointLight(0xffd39a, 2.4, 2800, 1.0);
    starLight.position.set(0, 0, 60);
    this.scene.add(starLight);
  }

  /** Set this frame's hull-laser impact points (world coords) — one per beam. */
  setImpacts(points: { x: number; y: number }[]): void {
    this.hullImpacts = points;
  }

  /** Show/hide the central-star glow (only present in the solar-system layer). */
  setStarGlow(visible: boolean): void {
    if (this.starGlow) this.starGlow.visible = visible;
  }

  /** Provide this frame's drone→asteroid beams (world coords). */
  setDroneBeams(beams: DroneBeam[]): void {
    this.droneBeams = beams;
  }

  /** Orient/scale the pooled cylinders to connect each drone to its target. */
  private updateDroneBeams(): void {
    for (let i = 0; i < this.beamMeshes.length; i++) {
      const mesh = this.beamMeshes[i]!;
      const b = this.droneBeams[i];
      if (!b) {
        mesh.visible = false;
        continue;
      }
      const dx = b.bx - b.ax;
      const dy = b.by - b.ay;
      const len = Math.hypot(dx, dy) || 1e-3;
      this.beamDir.set(dx / len, dy / len, 0);
      this.beamQuat.setFromUnitVectors(this.beamUp, this.beamDir);
      mesh.position.set((b.ax + b.bx) / 2, (b.ay + b.by) / 2, 0);
      mesh.quaternion.copy(this.beamQuat);
      mesh.scale.set(1, len, 1);
      mesh.visible = true;
    }
  }

  private hideSpark(i: number): void {
    this.dummy.position.set(1e7, 1e7, 0); // fully off-screen in XY
    this.dummy.scale.setScalar(0.0001);
    this.dummy.updateMatrix();
    this.sparkMesh.setMatrixAt(i, this.dummy.matrix);
  }

  /** Spawn one molten debris chunk at (x,y) that pops off the surface in +Z. */
  private emitSpark(x: number, y: number): void {
    const i = this.sparkCursor;
    this.sparkCursor = (this.sparkCursor + 1) % this.sparks.length;
    const s = this.sparks[i]!;
    const ang = Math.random() * Math.PI * 2;
    const spd = 22 + Math.random() * 78; // tighter spread — stays near the contact point
    s.x = x;
    s.y = y;
    s.z = 0;
    s.vx = Math.cos(ang) * spd;
    s.vy = Math.sin(ang) * spd;
    s.vz = 24 + Math.random() * 66; // launch toward the camera (+Z)
    s.max = 0.28 + Math.random() * 0.34;
    s.life = s.max;
    s.size = 0.5 + Math.random() * 0.8;
  }

  resize(width: number, height: number): void {
    this.renderer.setSize(width, height, false);
  }

  /** One VFX spark. Called internally by burst helpers. */
  private emit(p: Partial<Particle>): void {
    const slot = this.pool[this.cursor];
    if (!slot) return;
    slot.x = p.x ?? 0;
    slot.y = p.y ?? 0;
    slot.vx = p.vx ?? 0;
    slot.vy = p.vy ?? 0;
    slot.maxLife = p.maxLife ?? 0.7;
    slot.life = slot.maxLife;
    slot.size = p.size ?? 6;
    slot.r = p.r ?? 1;
    slot.g = p.g ?? 0.8;
    slot.b = p.b ?? 0.4;
    this.cursor = (this.cursor + 1) % this.pool.length;
  }

  /** Screen-shaking explosion at a WORLD position (Mechanic 1 VFX). */
  spawnExplosion(x: number, y: number, power = 1): void {
    const n = Math.floor(24 * power);
    for (let i = 0; i < n; i++) {
      const a = Math.random() * Math.PI * 2;
      const speed = 40 + Math.random() * 180 * power;
      this.emit({
        x, y,
        vx: Math.cos(a) * speed,
        vy: Math.sin(a) * speed,
        maxLife: 0.4 + Math.random() * 0.5,
        size: 4 + Math.random() * 6 * power,
        r: 1, g: 0.55 + Math.random() * 0.3, b: 0.2,
      });
    }
    this.shake(Math.min(0.5 + power * 0.4, 3));
  }

  /** Plasma torpedo trail spark at a world position. */
  spawnPlasma(x: number, y: number): void {
    this.emit({ x, y, maxLife: 0.5, size: 7, r: 0.5, g: 0.9, b: 1 });
  }

  /** A small, subtle flame lick off a badly-damaged hull (< 50% HP). Kept light
   * so the burning ship stays clearly visible. Call (throttled) per frame. */
  spawnFire(x: number, y: number): void {
    const a = Math.random() * Math.PI * 2;
    const spd = 6 + Math.random() * 20;
    this.emit({
      x: x + (Math.random() * 2 - 1) * 4,
      y: y + (Math.random() * 2 - 1) * 4,
      vx: Math.cos(a) * spd,
      vy: Math.sin(a) * spd - 16,
      maxLife: 0.2 + Math.random() * 0.18,
      size: 2 + Math.random() * 2.4,
      r: 0.85,
      g: 0.34,
      b: 0.08,
    });
  }

  /** Faint blue-white spark flick where a bolt strikes (subtle, semi-transparent). */
  spawnHit(x: number, y: number): void {
    for (let i = 0; i < 6; i++) {
      const a = Math.random() * Math.PI * 2;
      const speed = 24 + Math.random() * 70;
      this.emit({
        x, y,
        vx: Math.cos(a) * speed,
        vy: Math.sin(a) * speed,
        maxLife: 0.12 + Math.random() * 0.12,
        size: 2 + Math.random() * 2.5,
        // dim colours → additive blend stays faint / see-through
        r: 0.3, g: 0.42, b: 0.6,
      });
    }
  }

  shake(amp: number): void {
    this.shakeAmp = Math.max(this.shakeAmp, amp);
  }

  // TODO(combat): addBeam(from, to, color) using a pooled LineSegments mesh for
  // rapid-fire energy beams; addChargedBeam(...) with a growing cylinder mesh.

  /** Advance particles + shake, then draw. `dt` seconds; camera mirrors Pixi. */
  render(cam: Camera, viewW: number, viewH: number, dt: number): void {
    // update particle sim
    let alive = 0;
    for (let i = 0; i < this.pool.length; i++) {
      const p = this.pool[i]!;
      const idx = i * 3;
      if (p.life > 0) {
        p.life -= dt;
        p.x += p.vx * dt;
        p.y += p.vy * dt;
        p.vx *= 0.96;
        p.vy *= 0.96;
        const k = Math.max(p.life / p.maxLife, 0); // fade
        this.positions[idx] = p.x;
        this.positions[idx + 1] = p.y;
        this.positions[idx + 2] = 0;
        this.colors[idx] = p.r * k;
        this.colors[idx + 1] = p.g * k;
        this.colors[idx + 2] = p.b * k;
        alive++;
      } else {
        this.positions[idx] = 1e7; // dead points → off-screen (never at origin)
        this.positions[idx + 1] = 1e7;
        this.colors[idx] = this.colors[idx + 1] = this.colors[idx + 2] = 0;
      }
    }
    (this.geom.getAttribute("position") as BufferAttribute).needsUpdate = true;
    (this.geom.getAttribute("color") as BufferAttribute).needsUpdate = true;

    // --- molten sparks at every mining impact point (lit debris) ---
    // Mothership hull-laser sparks — one at EVERY beam's strike point.
    for (const p of this.hullImpacts) this.emitSpark(p.x, p.y);
    // …plus a spark at every drone beam strike on the rock.
    for (const b of this.droneBeams) this.emitSpark(b.bx, b.by);

    // Impact light follows the first hull strike, else the first drone strike.
    const first = this.droneBeams[0];
    const lightPt =
      this.hullImpacts[0] ?? (first ? { x: first.bx, y: first.by } : null);
    if (lightPt) {
      this.impactLight.position.set(lightPt.x, lightPt.y, 32);
      this.impactLight.intensity = 1.8 + Math.random() * 1.2; // flicker
    } else {
      this.impactLight.intensity *= Math.max(0, 1 - dt * 8);
    }
    let colorTouched = false;
    for (let i = 0; i < this.sparks.length; i++) {
      const s = this.sparks[i]!;
      if (s.life <= 0) continue;
      s.life -= dt;
      if (s.life <= 0) {
        this.hideSpark(i);
        continue;
      }
      s.vz -= 240 * dt; // gravity pulls chunks back toward the surface
      s.vx *= 0.985;
      s.vy *= 0.985;
      s.x += s.vx * dt;
      s.y += s.vy * dt;
      s.z += s.vz * dt;
      if (s.z < 0) {
        s.z = 0;
        s.vz *= -0.35; // bounce off the rock
      }
      const k = s.life / s.max;
      this.dummy.position.set(s.x, s.y, s.z);
      this.dummy.scale.setScalar(s.size * (0.35 + 0.65 * k));
      this.dummy.rotation.set(s.x * 0.25, s.y * 0.25, s.z * 0.3);
      this.dummy.updateMatrix();
      this.sparkMesh.setMatrixAt(i, this.dummy.matrix);
      // hot yellow → deep orange → dark as the chunk cools (diffuse under lights)
      this.tmpColor.setRGB(1, 0.32 + 0.5 * k, 0.08 * k);
      this.sparkMesh.setColorAt(i, this.tmpColor);
      colorTouched = true;
    }
    this.sparkMesh.instanceMatrix.needsUpdate = true;
    if (colorTouched && this.sparkMesh.instanceColor) {
      this.sparkMesh.instanceColor.needsUpdate = true;
    }

    // thin 3D drone orbit-beams
    this.updateDroneBeams();

    // camera mirror + shake
    let sx = 0, sy = 0;
    if (this.shakeAmp > 0.001) {
      sx = (Math.random() * 2 - 1) * this.shakeAmp * 8;
      sy = (Math.random() * 2 - 1) * this.shakeAmp * 8;
      this.shakeAmp -= this.shakeDecay * dt * this.shakeAmp;
      if (this.shakeAmp < 0.001) this.shakeAmp = 0;
    }
    const z = cam.zoom;
    // Match Pixi exactly: +X right, +Y down. Keep the STANDARD up vector (so X is
    // not mirrored) and invert top/bottom to flip Y downward instead.
    this.camera.left = -viewW / 2 / z;
    this.camera.right = viewW / 2 / z;
    this.camera.top = -viewH / 2 / z; // inverted → +Y is screen-down
    this.camera.bottom = viewH / 2 / z;
    this.camera.near = 1;
    this.camera.far = 4000;
    this.camera.up.set(0, 1, 0);
    this.camera.position.set(cam.x + sx, cam.y + sy, 800); // above the play plane
    this.lookAtV.set(cam.x + sx, cam.y + sy, 0);
    this.camera.lookAt(this.lookAtV);
    this.camera.updateProjectionMatrix();

    this.renderer.render(this.scene, this.camera);
    void alive;
  }

  destroy(): void {
    try {
      this.geom.dispose();
      this.renderer.dispose();
      this.renderer.forceContextLoss();
    } catch (err) {
      console.warn("[space-swarm] Three destroy warning:", err);
    }
  }
}

/** Build a soft radial-gradient texture for the star's glow sprite. */
function makeStarGlowTexture(): CanvasTexture {
  const size = 256;
  const c = document.createElement("canvas");
  c.width = c.height = size;
  const ctx = c.getContext("2d")!;
  const g = ctx.createRadialGradient(
    size / 2,
    size / 2,
    0,
    size / 2,
    size / 2,
    size / 2,
  );
  g.addColorStop(0.0, "rgba(255,244,214,1)");
  g.addColorStop(0.25, "rgba(255,206,130,0.75)");
  g.addColorStop(0.55, "rgba(255,150,70,0.22)");
  g.addColorStop(1.0, "rgba(255,120,50,0)");
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, size, size);
  return new CanvasTexture(c);
}
